<template>
  <ActionPanelShell
    :title="stepTitle"
    :subtitle="subtitle"
    :show-back="currentStep !== 'step1'"
    @close="closeSlideOutPanel"
    @back="apBack"
  >
    <div class="sp" data-test="send-form-panel">
      <div v-if="initialLoading" class="sp-skel" aria-hidden="true" data-test="send-panel-loading"><i></i><i></i><i></i><i></i></div>

      <!-- Step 1 — ONE searchable list: the practice's forms and packets, or its Wiki templates. -->
      <section v-else-if="currentStep === 'step1'" class="sp-sec" data-test="send-form-pick">
        <PanelNotice v-if="apErrorText" tone="error" dismissible data-test="send-panel-error" @dismiss="apDismissError">{{ apErrorText }}</PanelNotice>

        <PillTabs :value="formSource" :items="sourceTabs" aria-label="What to send" @change="onSourceChange" />

        <div v-if="formSource === 'forms' && selectedForms.length" class="sp-chips" data-test="send-form-chips">
          <span v-for="f in selectedForms" :key="f.FormIDForWeb" class="sp-chip">
            <i class="fa fa-file-text-o" aria-hidden="true"></i>
            <span class="sp-chip__name">{{ f.Name }}</span>
            <button type="button" class="sp-chip__x" :aria-label="`Remove ${f.Name}`" @click="updateSelectedForms(f)"><i class="fa fa-times" aria-hidden="true"></i></button>
          </span>
        </div>

        <TemplatePicker
          :key="formSource"
          :items="pickItems"
          :selected="pickedIds"
          :multiple="formSource === 'forms'"
          :search-placeholder="formSource === 'forms' ? 'Search forms and packets' : 'Search Wiki templates'"
          :empty-text="formSource === 'forms' ? 'No forms yet. Build one in Forms first.' : 'No Wiki templates tagged form yet.'"
          @toggle="onPickToggle"
        />
      </section>

      <!-- Step 2 — forms: the message ready to edit, and how to send it. -->
      <template v-else-if="currentStep === 'formStep2FromForms'">
        <section class="sp-sec" data-test="send-form-chosen">
          <PanelNotice v-if="apErrorText" tone="error" dismissible data-test="send-panel-error" @dismiss="apDismissError">{{ apErrorText }}</PanelNotice>
          <div class="sp-head">
            <span class="sp-h">Forms</span>
            <button type="button" class="sp-link sp-aside" data-test="send-form-change" @click="apBack">Change</button>
          </div>
          <ul class="sp-list">
            <li v-for="f in selectedForms" :key="f.FormIDForWeb">
              <i class="fa fa-file-text-o" aria-hidden="true"></i>
              <span>{{ f.Name }}</span>
            </li>
          </ul>
        </section>

        <section class="sp-sec" data-test="send-form-delivery">
          <div class="sp-h">Send by</div>

          <PanelNotice v-if="!deliveryTabs.length" tone="warn" data-test="send-form-no-delivery">
            {{ noDeliveryText }} Please contact TeamIO support to enable these features or update the patient’s contact information in Open Dental.
          </PanelNotice>

          <template v-else>
            <PillTabs v-if="deliveryTabs.length > 1" v-model="deliveryMethod" :items="deliveryTabs" aria-label="Send by" />
            <p class="sp-note" data-test="send-form-to-line">{{ toLine }}</p>

            <PanelNotice v-if="deliveryMethod === 'sms' && smsUnavailableReason" tone="warn" data-test="send-form-sms-unavailable">{{ smsUnavailableReason }}</PanelNotice>
            <PanelNotice v-if="deliveryMethod === 'email' && emailUnavailableReason" tone="warn" data-test="send-form-email-unavailable">{{ emailUnavailableReason }}</PanelNotice>

            <MessageComposer v-if="deliveryMethod === 'sms'">
              <template #field>
                <textarea
                  ref="smsTextarea"
                  v-model="smsMessage"
                  rows="7"
                  placeholder="Write your message"
                  aria-label="Text message"
                  data-test="send-form-sms-message"
                ></textarea>
              </template>
              <template #status>
                <SmsGuidance :text="smsMessage || ''" substitute-tags @change="onSmsGuidance" />
              </template>
            </MessageComposer>

            <MessageComposer v-else-if="deliveryMethod === 'email'">
              <template #field>
                <textarea
                  ref="emailTextarea"
                  v-model="emailMessage"
                  rows="7"
                  placeholder="Write your email"
                  aria-label="Email message"
                  data-test="send-form-email-message"
                ></textarea>
              </template>
            </MessageComposer>

            <PanelNotice v-if="deliveryMethod" tone="info" icon="fa-link" data-test="send-form-links-note">
              Each [Form: …] turns into a link only {{ firstName }} can open. Edit the words around it freely.
            </PanelNotice>
          </template>
        </section>
      </template>

      <!-- Step 2 — a Wiki template: it is sent by id, as written (and translated for the patient). -->
      <section v-else class="sp-sec" data-test="send-form-template">
        <PanelNotice v-if="apErrorText" tone="error" dismissible data-test="send-panel-error" @dismiss="apDismissError">{{ apErrorText }}</PanelNotice>
        <div class="sp-head">
          <span class="sp-h">Template</span>
          <button type="button" class="sp-link sp-aside" data-test="send-form-change" @click="apBack">Change</button>
        </div>

        <div v-if="selectedWiki.length" class="sp-tpl" data-test="send-form-template-card">
          <div class="sp-tpl__head">
            <i class="fa fa-file-text-o" aria-hidden="true"></i>
            <div>
              <div class="sp-tpl__name">{{ selectedWiki[0].Name }}</div>
              <div v-if="templateTags" class="sp-tpl__tags">{{ templateTags }}</div>
            </div>
          </div>
          <div class="sp-tpl__body">{{ selectedWikiSmsText }}</div>
        </div>

        <!-- Information only: this step shows no composed text, so show the template's credit cost. -->
        <SmsGuidance :text="selectedWikiSmsText" substitute-tags />
        <p class="sp-note" data-test="send-form-to-line">{{ smsToLine }}</p>
        <PanelNotice v-if="smsUnavailableReason" tone="warn" data-test="send-form-sms-unavailable">{{ smsUnavailableReason }}</PanelNotice>
      </section>
    </div>

    <template v-if="showFooter" #footer>
      <span class="ap-foot-note" data-test="send-form-foot-note"><span :class="{ 'sp-note--bad': footNoteBad }">{{ footNote }}</span></span>
      <button
        type="button"
        class="sp-foot-btn ap-foot-main"
        :disabled="footDisabled"
        data-test="send-form-main"
        @click="onFooterClick"
      >{{ footLabel }}</button>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * Send a form — the redesigned panel (design "Patient Action Panels" 2c, 3a).
 *
 * `extends` PatientSidebar: the forms / packets / Wiki template loading, the classic step machine
 * (step1 → formStep2FromForms | formStep2FromWiki), gotoStep2's message prefill, and every send
 * (sendText, sendCustomEmail, sendTextWithWiki) run the classic code. New: one searchable list with a
 * Forms | Wiki templates switch instead of two paginated tables, a send step with the message ready
 * to edit, and the footer button that names exactly what it will do.
 *
 * FIX 3 (classic bug): a form and a Wiki template could both be selected, and PatientSidebar.sendText
 * then sent the TEMPLATE id and silently dropped the form links. Here the switch clears the other
 * source's selection (onSourceChange), so only the chosen source reaches the send step.
 *
 * The SMS text shows `[Form: name]` placeholders (the same as the email and the Text panel's
 * templates) and fills templateLinkMap, so PatientSidebar.sendText turns each back into its link
 * anchor before sending — the text that goes out is the same.
 *
 * ⛔ Keeps `ref="smsTextarea"` / `ref="emailTextarea"` — insertAtCursor() in PatientSidebar uses them.
 */
import PatientSidebar from '@/pages/PatientProfile/components/PatientSidebar';
import SmsGuidance from '@/components/SmsGuidance.vue';
import ActionPanelShell from './ActionPanelShell.vue';
import MessageComposer from './MessageComposer.vue';
import TemplatePicker from './TemplatePicker.vue';
import PillTabs from './PillTabs.vue';
import PanelNotice from './PanelNotice.vue';
import sendPanelCommon, { formatPhoneLine } from './sendPanelCommon.js';
import { formLinkPlaceholders, linkMapFromPlaceholders } from '@/utils/emailBody.js';
import { tagsOf } from '@/utils/wikiTags.js';

export default {
  name: 'SendFormPanel',

  extends: PatientSidebar,
  mixins: [sendPanelCommon],

  components: { ActionPanelShell, MessageComposer, TemplatePicker, PillTabs, PanelNotice, SmsGuidance },

  props: {
    // The host passes no sidebarType: this panel IS the Form one. (PatientSidebar requires it.)
    sidebarType: { type: String, default: 'Form', validator: v => v === 'Form' },
  },

  data() {
    // 'forms' | 'wiki' — which list the switch shows, and the ONLY source the send step uses.
    return { formSource: 'forms' };
  },

  computed: {
    subtitle() {
      return this.apPatientName ? `To ${this.apPatientName}` : '';
    },
    firstName() {
      return (this.patientData && this.patientData.FName) || 'the patient';
    },
    stepTitle() {
      if (this.currentStep === 'formStep2FromForms') {
        const n = this.selectedForms.length;
        return `Send ${n} form${n === 1 ? '' : 's'}`;
      }
      if (this.currentStep === 'formStep2FromWiki') return 'Send a template';
      return 'Send a form';
    },

    sourceTabs() {
      return [
        { id: 'forms', label: 'Forms', count: (this.forms || []).length },
        { id: 'wiki', label: 'Wiki templates', count: (this.wikiPages || []).length },
      ];
    },
    pickItems() {
      if (this.formSource === 'wiki') {
        return (this.wikiPages || []).map(w => ({
          id: w.WikiPageID,
          name: w.Name,
          desc: tagsOf(w).filter(t => t !== 'form').join(' · '),
        }));
      }
      return (this.forms || []).map(f => ({
        id: f.FormIDForWeb,
        name: f.Name,
        desc: f._isPacket ? '' : (f.FormType || ''),
        badge: f._isPacket ? 'Packet' : '',
        badgeTone: 'blue',
      }));
    },
    pickedIds() {
      return this.formSource === 'wiki'
        ? this.selectedWiki.map(w => w.WikiPageID)
        : this.selectedForms.map(f => f.FormIDForWeb);
    },
    pickedCount() {
      return this.formSource === 'wiki' ? this.selectedWiki.length : this.selectedForms.length;
    },

    templateTags() {
      return this.selectedWiki.length ? tagsOf(this.selectedWiki[0]).filter(t => t !== 'form').join(' · ') : '';
    },

    // The delivery switch: the same two the classic radio offered, in the design's order.
    deliveryTabs() {
      const tabs = [];
      if (this.canSendSMS) tabs.push({ id: 'sms', label: 'Text' });
      if (this.canSendEmail) tabs.push({ id: 'email', label: 'Email' });
      return tabs;
    },
    toLine() {
      if (this.deliveryMethod === 'email') return `To ${(this.patientData && this.patientData.Email) || 'the patient’s email'}`;
      return this.smsToLine;
    },
    smsToLine() {
      const phone = formatPhoneLine(this.patientData && this.patientData.WirelessPhone);
      const from = this.formattedSmsNumber ? `, from ${(this.user && this.user.SiteName) || 'your office'} · ${this.formattedSmsNumber}` : '';
      return phone ? `To ${phone}${from}` : `Sends by text${from}`;
    },
    // PatientSidebar's "No Delivery Methods Available" explanation, as one sentence.
    noDeliveryText() {
      if (!this.smsFeatureEnabled && !this.emailFeatureEnabled) return 'SMS and Email features are not configured for your account.';
      if (!this.smsFeatureEnabled) return `SMS feature is not configured for your account.${!this.canSendEmail ? ' Additionally, this patient does not have an email address.' : ''}`;
      if (this.smsOutboundOff) return `${this.smsDisabledMessage} ${!this.emailFeatureEnabled ? 'Email is not configured for your account either.' : (!this.canSendEmail ? 'This patient does not have an email address.' : '')}`.trim();
      if (!this.emailFeatureEnabled) return `Email feature is not configured for your account.${!this.canSendSMS ? ' Additionally, this patient does not have a valid phone number.' : ''}`;
      return 'This patient does not have valid contact information for SMS or Email.';
    },

    // ---- the footer: one main button that says exactly what it will do ----
    showFooter() {
      if (this.initialLoading) return false;
      if (this.currentStep === 'formStep2FromForms') return this.deliveryTabs.length > 0;
      return true;
    },
    footLabel() {
      if (this.currentStep === 'step1') return 'Continue';
      if (this.apBusy) return 'Sending…';
      if (this.currentStep === 'formStep2FromWiki') return 'Send by text';
      return this.deliveryMethod === 'email' ? 'Send by email' : 'Send by text';
    },
    footNote() {
      if (this.currentStep === 'step1') {
        if (this.formSource === 'wiki') return this.pickedCount ? '1 template picked' : 'Pick a template';
        const n = this.pickedCount;
        return n ? `${n} form${n === 1 ? '' : 's'} picked` : 'Pick one or more forms';
      }
      if (this.currentStep === 'formStep2FromForms') {
        if (this.deliveryMethod === 'email') return this.emailCreditsBlocked ? 'Your practice is out of email credits.' : '1 email credit';
        return this.smsBlocked ? 'Too long to send as one text.' : '';
      }
      return '';
    },
    footNoteBad() {
      return (this.currentStep === 'formStep2FromForms')
        && (this.deliveryMethod === 'email' ? this.emailCreditsBlocked : this.smsBlocked);
    },
    footDisabled() {
      if (this.currentStep === 'step1') return this.pickedCount === 0;
      if (this.apBusy) return true;
      if (this.currentStep === 'formStep2FromWiki') return !!this.smsUnavailableReason || !this.selectedWiki.length;
      if (this.deliveryMethod === 'email') {
        return !!this.emailUnavailableReason || this.emailCreditsBlocked || !(this.emailMessage || '').trim();
      }
      return !!this.smsUnavailableReason || this.smsBlocked || !(this.smsMessage || '').trim();
    },
  },

  watch: {
    // A practice with no forms but some Wiki templates opens on the templates.
    initialLoading(loading) {
      if (!loading && !(this.forms || []).length && (this.wikiPages || []).length) this.formSource = 'wiki';
    },
  },

  methods: {
    /**
     * FIX 3. The switch clears BOTH selections: a form and a Wiki template can never be picked
     * together, so the send step only ever sees the source the user chose.
     */
    onSourceChange(source) {
      if (source === this.formSource) return;
      this.resetAllErrors();
      this.formSource = source;
      this.selectedForms = [];
      this.sendFormEmailLinks = [];
      this.selectedWiki = [];
      this.selectedTemplateHasFormLinks = false;
      this.templateFormLinks = [];
      this.templateLinkMap = {};
      this.templateTextModified = false;
    },

    // PatientSidebar's own selection methods: forms toggle in and out; a Wiki template is a single choice.
    onPickToggle(id) {
      if (this.formSource === 'wiki') {
        const row = (this.wikiPages || []).find(w => w.WikiPageID === id);
        if (row) this.updateSelectedWiki(row);
        return;
      }
      const row = (this.forms || []).find(f => f.FormIDForWeb === id);
      if (row) this.updateSelectedForms(row);
    },

    onFooterClick() {
      if (this.currentStep === 'step1') {
        this.resetAllErrors();
        this.gotoStep2(this.formSource);
        return undefined;
      }
      if (this.currentStep === 'formStep2FromWiki') return this.sendTextWithWiki();
      return this.deliveryMethod === 'email' ? this.sendCustomEmail() : this.sendText();
    },

    /**
     * The classic prefill writes `<a href="{{patient_link_for_form_ID}}">Name</a>` lines into the text
     * message. Show `[Form: Name]` instead — the form the email box already shows — and keep the
     * anchors in templateLinkMap, which PatientSidebar.sendText swaps back in before sending.
     */
    gotoStep2(from) {
      PatientSidebar.methods.gotoStep2.call(this, from);
      if (from !== 'forms' || this.currentStep !== 'formStep2FromForms') return;
      const entries = formLinkPlaceholders(this.selectedForms);
      const msg = this.smsMessage || '';
      const cut = msg.indexOf('<a href=');
      if (!entries.length || cut < 0) return;
      this.smsMessage = msg.slice(0, cut) + entries.map(e => e.placeholder).join('\n');
      // The anchors carry the RAW form name, exactly as the classic prefill wrote them: the entries'
      // anchors are HTML-escaped for the email, and processLink would text "&amp;" to the patient.
      // formLinkPlaceholders skips only rows without an id, so the filtered list lines up with entries.
      const rows = this.selectedForms.filter(f => f && (f._isPacket ? f.PacketID : f.FormIDForWeb));
      this.templateLinkMap = linkMapFromPlaceholders(entries.map((e, i) => ({
        placeholder: e.placeholder,
        anchor: e.anchor.replace(/>[^<]*<\/a>$/, () => `>${rows[i].Name}</a>`),
      })));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

// The footer's main button (shell hook .ap-foot-main fills the row on a phone).
.sp-foot-btn { @include ap-btn-primary; }

// The forms picked so far, as removable chips above the list.
.sp-chips { display: flex; flex-wrap: wrap; gap: 6px; }

.sp-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-height: 28px;
  padding: 0 4px 0 10px;
  border-radius: 8px;
  background: $pp-brand-soft;
  color: $teal-700;
  font-size: 12.5px;
  font-weight: 600;

  @include ap-touch { min-height: 36px; }
}

.sp-chip__name { min-width: 0; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.sp-chip__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: inherit;
  font-size: 11px;
  cursor: pointer;

  &:hover { background: $pp-brand-tint; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }

  @include ap-touch { width: 32px; height: 32px; }
}

// The Wiki template shown on its send step.
.sp-tpl {
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;
  overflow: hidden;
}

.sp-tpl__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid $pp-line;
  background: $pp-bg-2;

  > i { flex: none; color: $pp-ink-3; }
}

.sp-tpl__name { font-size: 14px; font-weight: 700; overflow-wrap: anywhere; }
.sp-tpl__tags { margin-top: 1px; font-size: 12px; color: $pp-ink-3; }

.sp-tpl__body {
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  color: $pp-ink-2;
  overflow-wrap: anywhere;
}
</style>
